/**
 * Mutually exclusive "Show" presets for the manifests list. Each preset maps to
 * omnisearch terms the M0/M1 backend understands. Counts for these presets are
 * loaded via `processedManifestShowFilterCounts` against the page scope
 * (tenant + year) only.
 */
export const SHOW_FILTER_IDS = [
  "allManifests",
  "overdueManifests",
  "awaitingEManifest",
  "awaitingEOverdueManifest",
  "unresolvedDiscrepancies",
  "pendingDiscrepancies",
  "transporterContacted",
  "exceptionReportFiled",
  "unlinkedManifests",
] as const;

export type ShowFilterId = (typeof SHOW_FILTER_IDS)[number];

/** Omnisearch keys owned by Show presets — stripped before applying a new one. */
export const SHOW_FILTER_OMNISEARCH_KEYS = [
  "correctionBucket",
  "isEManifest",
  "isUniformHazardousManifest",
  "unresolvedDiscrepancyCount",
  "pendingDiscrepancyCount",
  "transporterContacted",
  "exceptionReportFiled",
  "hasFacility",
] as const;

export interface ShowFilterDefinition {
  id: ShowFilterId;
  label: string;
  /** One-line explanation shown as a hover tooltip on the dropdown item. */
  description: string;
  terms: readonly string[];
}

export const SHOW_FILTERS: Record<ShowFilterId, ShowFilterDefinition> = {
  allManifests: {
    id: "allManifests",
    label: "All manifests",
    description: "Every manifest for the selected year — no status filter applied.",
    terms: [],
  },
  // "Corrections past due", not "Overdue corrections": the menu also carries
  // "Overdue e-Manifest" (a different, 45/60-day clock) two items below, and
  // the bare word was being read as the same deadline.
  overdueManifests: {
    id: "overdueManifests",
    label: "Corrections past due",
    description:
      "Manifests with an EPA correction request still unanswered more than 30 days after the request.",
    terms: ["correctionBucket:OVERDUE"],
  },
  awaitingEManifest: {
    id: "awaitingEManifest",
    label: "Awaiting e-Manifest",
    description: "Uniform-hazardous manifests with no e-Manifest record on file yet.",
    terms: ["isUniformHazardousManifest:true", "isEManifest:false"],
  },
  awaitingEOverdueManifest: {
    id: "awaitingEOverdueManifest",
    label: "Overdue e-Manifest",
    description:
      "Uniform-hazardous manifests still missing an e-Manifest past the 45/60-day deadline.",
    terms: ["isEManifest:overdue"],
  },
  unresolvedDiscrepancies: {
    id: "unresolvedDiscrepancies",
    label: "Manifests with discrepancies pending review",
    description:
      "Manifests with at least one discrepancy between sources that Encamp has not reviewed yet.",
    terms: ["unresolvedDiscrepancyCount:>0"],
  },
  pendingDiscrepancies: {
    id: "pendingDiscrepancies",
    label: "Manifests with a fix pending",
    description:
      "Manifests with a discrepancy whose fix is underway — a compliance action (TSDF contacted, exception report) is in flight.",
    terms: ["pendingDiscrepancyCount:>0"],
  },
  transporterContacted: {
    id: "transporterContacted",
    label: "TSDF contacted",
    description:
      "Manifests where the transporter/TSDF has been contacted about a missing e-Manifest.",
    terms: ["transporterContacted:true"],
  },
  exceptionReportFiled: {
    id: "exceptionReportFiled",
    label: "Exception Report Filed",
    description: "Manifests with a filed exception report for an overdue e-Manifest.",
    terms: ["exceptionReportFiled:true"],
  },
  unlinkedManifests: {
    id: "unlinkedManifests",
    label: "No facility link",
    description:
      "Manifests saved without a linked facility — the EPA ID didn't match a facility in Encamp (bad/unknown ID, or the facility isn't set up yet).",
    terms: ["hasFacility:false"],
  },
};

export const SHOW_FILTER_ORDER: readonly ShowFilterId[] = SHOW_FILTER_IDS;

/** Presets checked first win; more specific filters must precede broader ones. */
const SHOW_FILTER_DETECTION_ORDER: readonly ShowFilterId[] = [
  "overdueManifests",
  "awaitingEOverdueManifest",
  "awaitingEManifest",
  "unresolvedDiscrepancies",
  "pendingDiscrepancies",
  "transporterContacted",
  "exceptionReportFiled",
  "unlinkedManifests",
  "allManifests",
];

function tokenize(search: string): string[] {
  return search.split(/\s+/).filter((token) => token.length > 0);
}

function tokenKey(token: string): string | null {
  const colonIdx = token.indexOf(":");
  if (colonIdx <= 0) return null;
  return token.substring(0, colonIdx);
}

/** Which Show preset matches the active omnisearch string, if any. */
export function detectActiveShowFilter(search: string): ShowFilterId {
  const tokens = tokenize(search);

  for (const id of SHOW_FILTER_DETECTION_ORDER) {
    const preset = SHOW_FILTERS[id];
    if (preset.terms.length === 0) {
      continue;
    }
    if (preset.terms.every((term) => tokens.includes(term))) {
      return id;
    }
  }

  const hasShowKey = tokens.some((token) => {
    const key = tokenKey(token);
    return (
      key != null &&
      SHOW_FILTER_OMNISEARCH_KEYS.includes(key as (typeof SHOW_FILTER_OMNISEARCH_KEYS)[number])
    );
  });
  return hasShowKey ? "allManifests" : "allManifests";
}

/** Replace Show-preset tokens in `search` with those from `filterId`. */
export function applyShowFilterToSearch(search: string, filterId: ShowFilterId): string {
  const showKeySet = new Set<string>(SHOW_FILTER_OMNISEARCH_KEYS);
  const kept = tokenize(search).filter((token) => {
    const key = tokenKey(token);
    return key == null || !showKeySet.has(key);
  });
  const nextTerms = SHOW_FILTERS[filterId].terms;
  const merged = kept.concat(nextTerms.filter((term) => !kept.includes(term)));
  return merged.join(" ");
}

export function formatShowFilterLabel(label: string, count: number | undefined): string {
  if (count == null) return label;
  return `${label} (${count.toLocaleString()})`;
}
